/**
 * Helpers for the warehouse call-off wizard stock grid (WarehouseDeliveryModal):
 * local (per-browser) column preferences + small cell/row formatters. Extracted
 * so the modal stays within the file-size limit and the column-prefs logic can
 * be unit-tested in isolation.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import type { CallOffCustomerConfig, StockPlanningItem } from '../../../lib/api';
import type { LocalColumn } from './DirectDeliveryColumns';
import { blankCellText } from '../shared/directsColumns';

type WarehouseLabelConfig = Pick<
  CallOffCustomerConfig,
  'primary_warehouse_column_label' | 'secondary_warehouse_label' | 'secondary_warehouse_column_label'
>;

/**
 * Apply the customer's warehouse naming to the wizard columns.
 *
 * The column ids stay fixed (`sum_on_saltney` / `sum_on_mold`) so stored
 * column preferences survive a rename; only the header copy follows the
 * customer's CallOffCustomerConfig. CR-7744: a customer with no secondary
 * warehouse has no Mold figure to show, so that column is dropped — the same
 * rule the supplier Stock Planning tab applies.
 *
 * @param cols - the wizard's columns, in any order.
 * @param config - the customer's call-off config (labels only).
 * @returns the columns relabelled, minus Mold when there is no secondary warehouse.
 */
export function applyWarehouseLabels(cols: LocalColumn[], config: WarehouseLabelConfig): LocalColumn[] {
  const hasSecondary = !!(config.secondary_warehouse_label || '').trim();
  return cols
    .filter(c => c.id !== 'sum_on_mold' || hasSecondary)
    .map(c => {
      if (c.id === 'sum_on_saltney') return { ...c, label: config.primary_warehouse_column_label || c.label };
      if (c.id === 'sum_on_mold') return { ...c, label: config.secondary_warehouse_column_label || c.label };
      return c;
    });
}

/** Red when any health value is negative, amber when low, otherwise no tint. */
export function getStockHealthColor(min: string, max: string): string {
  const minVal = parseFloat(min) || 0;
  const maxVal = parseFloat(max) || 0;
  if (minVal < 0 || maxVal < 0) return 'bg-red-50';
  if (minVal < 50 || maxVal < 50) return 'bg-yellow-50';
  return '';
}

/** Get a cell value from a stock item based on column id. */
export function getItemCellValue(item: StockPlanningItem, colId: string): string {
  const val = item[colId as keyof StockPlanningItem];
  return val !== undefined && val !== null ? String(val) : blankCellText(colId);
}

const COLUMN_PREFS_KEY = 'call-off:warehouse-wizard:columns';

/**
 * Merge persisted column prefs with the canonical defaults. New columns added
 * since the prefs were saved fall back to their default visibility and sort
 * order so we don't permanently hide them.
 */
function mergeWithDefaults(defaults: LocalColumn[], saved: Partial<LocalColumn>[] | null): LocalColumn[] {
  if (!saved || !Array.isArray(saved)) return defaults;
  const byId = new Map(saved.map(c => [c.id, c]));
  return defaults.map(d => {
    const s = byId.get(d.id);
    return s
      ? { ...d, visible: s.visible ?? d.visible, sortOrder: s.sortOrder ?? d.sortOrder }
      : d;
  });
}

function loadStoredColumns(defaults: LocalColumn[]): LocalColumn[] {
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = window.localStorage.getItem(COLUMN_PREFS_KEY);
    return mergeWithDefaults(defaults, raw ? JSON.parse(raw) : null);
  } catch {
    return defaults;
  }
}

/**
 * Manage the wizard's column visibility + order, persisted per-browser in
 * localStorage. Returns the full column list, the visible-in-order subset, and
 * toggle / move-up mutators.
 */
export function useLocalColumns(defaults: LocalColumn[]) {
  const [columns, setColumns] = useState<LocalColumn[]>(() => loadStoredColumns(defaults));

  useEffect(() => {
    try {
      const slim = columns.map(c => ({ id: c.id, visible: c.visible, sortOrder: c.sortOrder }));
      window.localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(slim));
    } catch {
      // localStorage full or disabled — silently ignore.
    }
  }, [columns]);

  const toggle = useCallback((columnId: string) => {
    setColumns(prev => prev.map(c =>
      c.id === columnId ? { ...c, visible: !c.visible } : c
    ));
  }, []);

  const moveUp = useCallback((columnId: string) => {
    setColumns(prev => {
      const sorted = [...prev].sort((a, b) => a.sortOrder - b.sortOrder);
      const idx = sorted.findIndex(c => c.id === columnId);
      if (idx <= 0) return prev;
      return sorted.map((col, i) => {
        if (i === idx) return { ...col, sortOrder: i - 1 };
        if (i === idx - 1) return { ...col, sortOrder: i + 1 };
        return { ...col, sortOrder: i };
      });
    });
  }, []);

  const visible = useMemo(
    () => [...columns].sort((a, b) => a.sortOrder - b.sortOrder).filter(c => c.visible),
    [columns]
  );

  return { columns, visible, toggle, moveUp };
}

/**
 * CR-7342: the quantity a buyer may call off for one stock-planning row.
 *
 * The rule itself lives server-side in `availability.callable_qty_map`, which
 * the pre-send coverage check and the inline editor also read. Deriving it here
 * as well is what let the grid green-light a quantity that Send then refused,
 * so this deliberately does no arithmetic beyond the edit add-back.
 *
 * `own` is the qty this delivery line already held at edit-mode entry. The
 * server counts it as demand like anyone else's, so without adding it back,
 * reopening your own saved line would read as over its own cap.
 *
 * @param item - the stock-planning row.
 * @param own - qty this line already held (0 when adding a new one).
 * @returns the maximum quantity the buyer may enter for this row.
 */
export function effectiveCallOffCap(
  item: Pick<StockPlanningItem, 'max_callable_qty'>,
  own: number,
): number {
  return (parseFloat(item.max_callable_qty as unknown as string) || 0) + own;
}

/**
 * Why a quantity is over this row's limit, in the figures shown beside it.
 *
 * The limit is whichever is larger of two routes (`availability.callable_qty_map`):
 * the open PO left to call off (the Available to Call Off column), or the
 * supplier's stock less what is already called off and not yet shipped. The
 * message names the route that set it, so every number reconciles with a
 * visible column — and never puts two numbers either side of a dash, which
 * read as a range ("Only 105 — 210 available…").
 *
 * @param item - the stock-planning row.
 * @param cap - the most this row may take (see effectiveCallOffCap).
 * @returns e.g. "Up to 105 can be called off (210 in supplier stock, 105 already called off)".
 */
export function overLimitReason(
  item: Pick<StockPlanningItem, 'stock_with_supplier' | 'available_to_call_off' | 'committed_qty' | 'max_callable_qty'>,
  cap: number,
): string {
  const stock = parseFloat(item.stock_with_supplier as unknown as string) || 0;
  const openPo = parseFloat(item.available_to_call_off as unknown as string) || 0;
  const committed = parseFloat(item.committed_qty as unknown as string) || 0;
  const callable = parseFloat(item.max_callable_qty as unknown as string) || 0;
  if (cap <= 0) {
    return committed > 0
      ? `Nothing left to call off (${committed} already called off)`
      : 'Nothing available to call off';
  }
  // The stock route set the limit only when it beat the open PO.
  if (callable > openPo) {
    const called = Math.max(stock - cap, 0);
    return called > 0
      ? `Up to ${cap} can be called off (${stock} in supplier stock, ${called} already called off)`
      : `Up to ${cap} can be called off (${stock} in supplier stock)`;
  }
  return `Up to ${cap} can be called off (open PO left to call off)`;
}
